import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  extend, useLazyDataModel, params, string, arrayOf, shape, number, customType
} from '@thd-nucleus/data-sources';
import {
  getIntentWithIdMap, getItemsToResolveWithMissingData, itemsResolveBulkIntents
} from '../../utils/intents.utils';

export const useAddNewIntentsProject = ({ projectId, projectRefetch, defaultListId, defaultListMutationKey }) => {
  const [createIntentsProject] = useLazyDataModel('addIntentsToProject', {
    variables: {
      projectId,
    },
    skip: !projectId,
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });
  const [resolveBulkIntents] = useLazyDataModel('resolveBulkIntents', {
    variables: {
      mutationKey: defaultListMutationKey,
      listId: defaultListId
    },
    skip: !projectId,
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  const handleCreateNewIntents = async (currentIntents) => {
    try {
      const isQuickAutoResolve = currentIntents.some((intent) => intent.materials.some((material) => material.itemId));
      const itemsToAdd = currentIntents.map((grpIntent) => {
        return {
          groupName: grpIntent.groupName,
          items: grpIntent.materials.map((item) => ({
            description: item.intent,
            quantity: item.quantity
          }))
        };
      });
      await createIntentsProject({
        variables: {
          intents: itemsToAdd,
        }
      });

      const res = await projectRefetch();

      if (isQuickAutoResolve) {
        const itemGroups = res?.data?.projectDetails?.itemGroups ?? null;

        const intentsIds = getIntentWithIdMap(itemGroups);
        const itemsToResolve = itemsResolveBulkIntents(currentIntents, intentsIds);
        const allItemsToResolveMissingData = getItemsToResolveWithMissingData(currentIntents);
        if (allItemsToResolveMissingData.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            'The following items are missing required data for auto-resolve and will not be resolved:',
            allItemsToResolveMissingData);
        }

        await resolveBulkIntents({
          variables: {
            intents: itemsToResolve
          }
        });
        await projectRefetch();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error creating new intents in project:', error);
    }
  };

  return {
    handleCreateNewIntents,
  };
};

useAddNewIntentsProject.propTypes = {
  projectId: PropTypes.string.isRequired,
  projectRefetch: PropTypes.func.isRequired,
  defaultListMutationKey: PropTypes.string.isRequired,
  defaultListId: PropTypes.string.isRequired
};

useAddNewIntentsProject.dataModel = extend(
  {
    addIntentsToProject: params({
      projectId: string().isRequired(),
      intents: arrayOf(customType('CreateIntentInput').shape({
        groupName: string().isRequired(),
        items: arrayOf(shape({
          description: string().isRequired(),
          quantity: number().isRequired()
        }).isRequired()).isRequired()
      }).isRequired()).isRequired()
    }).mutation().shape({
      projectId: string()
    }),
    resolveBulkIntents: params({
      listId: string().isRequired(),
      mutationKey: string().isRequired(),
      intents: arrayOf(customType('ResolveIntentsItemRequest').shape({
        purchaseIntent: string().isRequired(),
        itemType: string().isRequired(),
        quantity: number().isRequired(),
        sku: string().isRequired(),
        itemId: string().isRequired(),
        omsId: string().isRequired()
      }).isRequired()).isRequired()
    }).mutation().shape({
      projectId: string()
    })
  }
);
